import { OnboardSection } from './sections/OnboardSection';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { AuthContextMembership, AuthContextResponse, CapabilityCode } from '@railroute/contracts/core';
import { ApiError, fetchAuthContext, login } from './api';
import { readOrganizationId, readToken, writeOrganizationId, writeToken } from './auth';
import { PANEL_SECTIONS, keepSection, sectionsFor, type SectionId } from './sections';
import { OrganizationSection } from './sections/OrganizationSection';
import { PersonnelSection } from './sections/PersonnelSection';
import { SubdivisionsSection } from './sections/SubdivisionsSection';
import { WorkOrderIssueSection } from './sections/WorkOrderIssueSection';
import { WorkOrderSection } from './sections/WorkOrderSection';
import { RouteSheetsSection } from './sections/RouteSheetsSection';
import { BriefingJournalSection } from './sections/BriefingJournalSection';

// Оболочка панели: вход теми же учётными данными, что в телефоне, выбор
// организации и раскладка разделов по правам.
//
// ПРАВА СЧИТАЕТ СЕРВЕР. Панель берёт их готовыми из `GET /auth/context` — по
// одному набору на каждое членство — и ничего к ним не добавляет. Своей модели
// прав у неё нет и быть не должно: вторая модель неизбежно разойдётся с первой,
// и разойдётся молча.
//
// ОРГАНИЗАЦИЯ ВЫБИРАЕТСЯ ЯВНО и уходит заголовком на каждом запросе. У
// человека, работающего в двух организациях, права в них разные — сложить их
// в один набор значит показать ему в одной организации разделы другой.

export default function App() {
  const [token, setToken] = useState<string | null>(() => readToken());
  const [context, setContext] = useState<AuthContextResponse | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(() => readOrganizationId());
  const [section, setSection] = useState<SectionId | null>(null);
  // Решение Штаба 30.09.2026: переход из журнала инструктажа к наряду —
  // номер, с которым открыть раздел «Наряд-допуск».
  const [workOrderNumber, setWorkOrderNumber] = useState<string | null>(null);

  const signOut = useCallback(() => {
    setToken(null);
    setContext(null);
    setContextError(null);
    setSection(null);
  }, []);

  useEffect(() => {
    writeToken(token);
  }, [token]);

  useEffect(() => {
    writeOrganizationId(organizationId);
  }, [organizationId]);

  useEffect(() => {
    if (token === null) return;
    let alive = true;
    fetchAuthContext(token).then(
      (loaded) => {
        if (!alive) return;
        setContext(loaded);
        setContextError(null);
      },
      (error: unknown) => {
        if (!alive) return;
        if (error instanceof ApiError && error.status === 401) {
          signOut();
          return;
        }
        setContextError(error instanceof Error ? error.message : 'Права не загрузились');
      },
    );
    return () => {
      alive = false;
    };
  }, [token, signOut]);

  // Членство в отключённой организации или отключённое само — не выбор:
  // сервер по нему контекст не соберёт, и показывать его в списке значит
  // предлагать вход, который закончится отказом.
  const usable = (context?.memberships ?? []).filter(
    (membership) => membership.membershipIsActive && membership.organizationIsActive,
  );
  const selected =
    usable.find((membership) => membership.organizationId === organizationId) ??
    (usable.length === 1 ? usable[0] : null);
  const selectedId = selected?.organizationId ?? null;
  const capabilities = selected?.capabilities ?? [];
  const hints = { workOrderSectionVisible: selected?.workOrderSectionVisible };
  const available = sectionsFor(capabilities, hints).filter(item => item.id !== "onboard" || (selected?.enabledProducts.includes("RAIL") && selected?.membershipProducts.includes("RAIL")));
  // Смена организации меняет и набор прав: выбранный раздел остаётся, только
  // если он есть и в новой организации. Считается ПРИ ОТРИСОВКЕ, а не в
  // эффекте после неё: иначе первым кадром после смены организации успевает
  // мелькнуть раздел, на который в ней прав нет, и человек видит отказ
  // сервера вместо содержимого.
  const suggestedSection = keepSection(section, capabilities, hints);
  const activeSection = available.some(item => item.id === suggestedSection) ? suggestedSection : available[0]?.id ?? null;

  // Единственное членство выбирается само — запомнить надо именно его, а не
  // то, что осталось в хранилище от прошлой организации.
  useEffect(() => {
    if (selectedId !== null) setOrganizationId(selectedId);
  }, [selectedId]);

  if (token === null) return <LoginForm onToken={setToken} />;

  if (contextError !== null) {
    return (
      <Notice title="Права не загрузились" onSignOut={signOut}>
        {contextError}
      </Notice>
    );
  }

  if (context === null) return <Notice title="Загружаю…">Панель спрашивает сервер, что вам доступно.</Notice>;

  if (usable.length === 0) {
    return (
      <Notice title="Вы не числитесь ни в одной организации" onSignOut={signOut}>
        Действующего членства нет. Пока его не заведут, показывать в панели нечего.
      </Notice>
    );
  }

  if (selected === null) {
    return <OrganizationPicker memberships={usable} onPick={setOrganizationId} onSignOut={signOut} />;
  }

  return (
    <>
      <div className="appbar toolbar screen-only">
        <span className="appbar__brand">Ortaq Hub</span>
        {usable.length === 1 ? (
          <span className="appbar__org">{selected.organizationName}</span>
        ) : (
          <select
            className="ui-input appbar__org-select"
            value={selected.organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            {usable.map((membership) => (
              <option key={membership.organizationMembershipId} value={membership.organizationId}>
                {membership.organizationName}
              </option>
            ))}
          </select>
        )}
        <div className="appbar__actions">
          <button className="ui-btn ui-btn--ghost" onClick={signOut}>
            Выйти
          </button>
        </div>
      </div>
      {available.length === 0 ? (
        <div className="panel">
          <h2 className="section__title">Разделов для вас нет</h2>
          <p className="section__hint">
            В панели ведут кадры, справочники и журналы — прав на это у вас нет. Работа по нарядам идёт в
            мобильном приложении.
          </p>
        </div>
      ) : (
        <div className="panel">
          <nav className="panel__nav screen-only">
            {available.map((item) => (
              <button
                key={item.id}
                className={item.id === activeSection ? 'panel__tab panel__tab--active' : 'panel__tab'}
                onClick={() => setSection(item.id)}
              >
                {item.title}
              </button>
            ))}
          </nav>
          <SectionBody
            section={activeSection}
            token={token}
            organizationId={selected.organizationId}
            organizationMembershipId={selected.organizationMembershipId}
            // Подразделение выдающего берётся из его же членства: наряд
            // выписывается в подразделение, и выбирать его в форме нечего —
            // ровно как в телефоне, где оно приходит из профиля входа.
            subdivisionId={selected.membershipSubdivisionId ?? null}
            // Issue #445: разделу нужны не только его собственные права —
            // кнопка согласования внутри держится на WORK_ORDER_APPROVE, как
            // сам раздел держится на WORK_ORDER_READ.
            capabilities={capabilities}
            // Переход к наряду — только если раздел «Наряд-допуск» открыт:
            // кнопка, ведущая в закрытый раздел, хуже её отсутствия.
            onOpenWorkOrder={
              available.some((item) => item.id === 'work-order')
                ? (number: string) => {
                    setWorkOrderNumber(number);
                    setSection('work-order');
                  }
                : undefined
            }
            workOrderNumber={workOrderNumber}
            onUnauthorized={signOut}
          />
        </div>
      )}
    </>
  );
}

function SectionBody({
  section,
  token,
  organizationId,
  organizationMembershipId,
  subdivisionId,
  capabilities,
  onOpenWorkOrder,
  workOrderNumber,
  onUnauthorized,
}: {
  section: SectionId | null;
  token: string;
  organizationId: string;
  organizationMembershipId: string;
  subdivisionId: string | null;
  capabilities: readonly CapabilityCode[];
  onOpenWorkOrder?: (workOrderNumber: string) => void;
  workOrderNumber: string | null;
  onUnauthorized: () => void;
}) {
  if (section === null) return null;
  const described = PANEL_SECTIONS.find((item) => item.id === section);
  const shared = { token, organizationId, onUnauthorized };

  return (
    <section className="section">
      {/* Журнал инструктажа держит свою шапку: справа у неё печать и Excel. */}
      {section !== 'briefing-journal' ? (
      <header className="section__head screen-only">
        <h2 className="section__title">{described?.title}</h2>
        <p className="section__hint">{described?.hint}</p>
      </header>
      ) : null}
      {/* Issue #524: разделу нужны права — ввод и продление допуска в карточке
          работника держатся на CLEARANCE_EVIDENCE_MANAGE, как раздел держится
          на PERSONNEL_READ. */}
      {section === 'onboard' ? <OnboardSection key={organizationId} {...shared} organizationMembershipId={organizationMembershipId} onOpenWorkOrder={onOpenWorkOrder} /> : null}
      {section === 'personnel' ? <PersonnelSection {...shared} capabilities={capabilities} /> : null}
      {/* Журнал регистрации инструктажа — на BRIEFING_JOURNAL_READ, как и
          ручка под ним (решение Штаба 30.09.2026); охват и номера нарядов
          решает сервер. */}
      {section === 'briefing-journal' ? <BriefingJournalSection {...shared} onOpenWorkOrder={onOpenWorkOrder} /> : null}
      {section === 'subdivisions' ? <SubdivisionsSection {...shared} /> : null}
      {section === 'organization' ? <OrganizationSection {...shared} /> : null}
      {/* Issue #445: разделу нужны права — кнопка согласования держится на
          WORK_ORDER_APPROVE, как раздел держится на WORK_ORDER_READ. */}
      {section === 'work-order' ? (
        <WorkOrderSection {...shared} capabilities={capabilities} initialNumber={workOrderNumber} />
      ) : null}
      {section === 'work-order-issue' ? <WorkOrderIssueSection {...shared} subdivisionId={subdivisionId} /> : null}
      {/* Issue #508: выбор работника держится на ROUTE_ASSIGN — том же праве,
          на котором стоит список людей подразделения. Чей лист виден, решает
          сервер, а не панель. */}
      {section === 'route-sheets' ? <RouteSheetsSection {...shared} capabilities={capabilities} /> : null}
    </section>
  );
}

function OrganizationPicker({
  memberships,
  onPick,
  onSignOut,
}: {
  memberships: readonly AuthContextMembership[];
  onPick: (organizationId: string) => void;
  onSignOut: () => void;
}) {
  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <h1 className="auth__title">Ortaq Hub</h1>
          <p className="auth__subtitle">Выберите организацию</p>
        </div>
        {memberships.map((membership) => (
          <button
            key={membership.organizationMembershipId}
            className="ui-btn ui-btn--ghost auth__choice"
            onClick={() => onPick(membership.organizationId)}
          >
            {membership.organizationName}
            <span className="auth__choice-code">{membership.organizationCode}</span>
          </button>
        ))}
        <button className="ui-btn ui-btn--ghost" onClick={onSignOut}>
          Выйти
        </button>
      </div>
    </div>
  );
}

function Notice({
  title,
  children,
  onSignOut,
}: {
  title: string;
  children: ReactNode;
  onSignOut?: () => void;
}) {
  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <h1 className="auth__title">{title}</h1>
        </div>
        <p className="auth__subtitle">{children}</p>
        {onSignOut ? (
          <button className="ui-btn ui-btn--ghost" onClick={onSignOut}>
            Выйти
          </button>
        ) : null}
      </div>
    </div>
  );
}

function LoginForm({ onToken }: { onToken: (token: string) => void }) {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('SENIOR');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onToken(await login(employeeNumber.trim(), password, role));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Вход не выполнен');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <form className="auth__card" onSubmit={submit}>
        <div className="auth__brand">
          <h1 className="auth__title">Ortaq Hub</h1>
          <p className="auth__subtitle">Кадры, справочники, наряды — из браузера</p>
        </div>
        <label className="auth__field">
          <span className="auth__label">Табельный номер</span>
          <input
            className="ui-input"
            value={employeeNumber}
            onChange={(e) => setEmployeeNumber(e.target.value)}
            autoComplete="username"
          />
        </label>
        <label className="auth__field">
          <span className="auth__label">Пароль</span>
          <input
            className="ui-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        <label className="auth__field">
          <span className="auth__label">Роль</span>
          {/* Названия ролей — те же, что в приложении (locales/ru.json, role.*).
              Значения машинные и не меняются: их читает сервер. */}
          <select className="ui-input" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="WORKER">Работник</option>
            <option value="SENIOR">Старший</option>
            <option value="ADMIN">Руководитель</option>
          </select>
        </label>
        {error ? <p className="ui-error">{error}</p> : null}
        <button className="ui-btn ui-btn--primary" type="submit" disabled={busy}>
          {busy ? 'Вхожу…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}
